import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { fetchBackendJson, hasBackendApiConfig } from '../lib/backendApi';

const minimalContent = {
  siteContent: {
    siteInfo: { name: 'Sybil Esi', tagline: '', email: '' },
    navigation: { links: [], ctaButton: { text: '', link: '/' } },
    socialLinks: [],
    newsletter: {},
  },
  homeContent: { hero: {}, about: {}, testimonials: [] },
  servicesContent: { sectionTitle: '', sectionDescription: '', services: [] },
  venturesContent: { sectionTitle: '', sectionLabel: '', sectionDescription: '', ventures: [] },
  shopContent: { hero: {}, products: [] },
  faqContent: { title: '', sections: [] },
  aboutContent: { hero: {}, tabs: [] },
  resourcesContent: { hero: {}, sections: { books: { books: [] }, newsletter: {}, waitlist: null } },
  communityContent: { hero: {}, overview: {}, gallery: { images: [] }, benefits: [], testimonials: [], cta: {}, joinForm: {} },
  contactContent: { hero: {}, inquiryPaths: [], form: {}, contactInfo: {} },
  pressKitContent: { hero: {}, sections: {} },
  bookLandingContent: {},
  blogContent: {
    title: 'The Blog',
    description: 'Reflections, lessons, and practical encouragement.',
    featuredLabel: 'Featured Story',
    allPostsLabel: 'Recent Posts',
    emptyStateTitle: 'Fresh stories are on the way',
    emptyStateDescription: 'Published posts will appear here soon.',
    posts: [],
  },
};

const ContentContext = createContext(null);

const SITE_CONTENT_CACHE_KEY = 'sybil_site_content_v1';

const readCachedSiteContent = () => {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(SITE_CONTENT_CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return null;
    return normalizeContentShape(parsed);
  } catch {
    return null;
  }
};

const writeCachedSiteContent = (payload) => {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(SITE_CONTENT_CACHE_KEY, JSON.stringify(payload));
  } catch {
    // Ignore quota / private-mode failures.
  }
};

const asObject = (value) => (value && typeof value === 'object' && !Array.isArray(value) ? value : {});
const asArray = (value) => (Array.isArray(value) ? value : []);

const normalizeContentShape = (rawContent = {}) => {
  const siteContent = asObject(rawContent.siteContent);
  const homeContent = asObject(rawContent.homeContent);
  const homeAbout = asObject(homeContent.about);
  const homeTestimonials = asObject(homeContent.testimonials);
  const resourcesContent = asObject(rawContent.resourcesContent);
  const resourceSections = asObject(resourcesContent.sections);
  const communityContent = asObject(rawContent.communityContent);
  const contactContent = asObject(rawContent.contactContent);
  const contactForm = asObject(contactContent.form);
  const contactInfo = asObject(contactContent.contactInfo);
  const faqContent = asObject(rawContent.faqContent);
  const pressKitContent = asObject(rawContent.pressKitContent);
  const blogContent = asObject(rawContent.blogContent);

  return {
    ...minimalContent,
    ...rawContent,
    siteContent: {
      ...minimalContent.siteContent,
      ...siteContent,
      siteInfo: { ...minimalContent.siteContent.siteInfo, ...asObject(siteContent.siteInfo) },
      navigation: {
        ...minimalContent.siteContent.navigation,
        ...asObject(siteContent.navigation),
        links: asArray(siteContent.navigation?.links),
        ctaButton: { ...minimalContent.siteContent.navigation.ctaButton, ...asObject(siteContent.navigation?.ctaButton) },
      },
      socialLinks: Array.isArray(siteContent.socialLinks) ? {} : asObject(siteContent.socialLinks),
      newsletter: { ...minimalContent.siteContent.newsletter, ...asObject(siteContent.newsletter) },
    },
    homeContent: {
      ...minimalContent.homeContent,
      ...homeContent,
      hero: { ...minimalContent.homeContent.hero, ...asObject(homeContent.hero) },
      about: {
        ...homeAbout,
        bio: asObject(homeAbout.bio),
        stats: asArray(homeAbout.stats),
        socialProof: {
          ...asObject(homeAbout.socialProof),
          logos: asArray(homeAbout.socialProof?.logos),
        },
      },
      testimonials: {
        ...homeTestimonials,
        items: asArray(homeTestimonials.items),
      },
    },
    servicesContent: {
      ...minimalContent.servicesContent,
      ...asObject(rawContent.servicesContent),
      services: asArray(rawContent.servicesContent?.services),
    },
    venturesContent: {
      ...minimalContent.venturesContent,
      ...asObject(rawContent.venturesContent),
      ventures: asArray(rawContent.venturesContent?.ventures),
    },
    shopContent: {
      ...minimalContent.shopContent,
      ...asObject(rawContent.shopContent),
      hero: { ...minimalContent.shopContent.hero, ...asObject(rawContent.shopContent?.hero) },
      products: asArray(rawContent.shopContent?.products),
    },
    aboutContent: {
      ...minimalContent.aboutContent,
      ...asObject(rawContent.aboutContent),
      hero: { ...minimalContent.aboutContent.hero, ...asObject(rawContent.aboutContent?.hero) },
      tabs: asArray(rawContent.aboutContent?.tabs),
    },
    resourcesContent: {
      ...minimalContent.resourcesContent,
      ...resourcesContent,
      hero: { ...minimalContent.resourcesContent.hero, ...asObject(resourcesContent.hero) },
      sections: {
        ...resourceSections,
        books: {
          ...asObject(resourceSections.books),
          books: asArray(resourceSections.books?.books),
        },
        newsletter: asObject(resourceSections.newsletter),
        waitlist: resourceSections.waitlist || null,
      },
    },
    communityContent: {
      ...minimalContent.communityContent,
      ...communityContent,
      hero: { ...minimalContent.communityContent.hero, ...asObject(communityContent.hero) },
      overview: { ...minimalContent.communityContent.overview, ...asObject(communityContent.overview) },
      gallery: {
        ...minimalContent.communityContent.gallery,
        ...asObject(communityContent.gallery),
        images: asArray(communityContent.gallery?.images),
      },
      benefits: asArray(communityContent.benefits),
      testimonials: asArray(communityContent.testimonials),
      cta: {
        ...minimalContent.communityContent.cta,
        ...asObject(communityContent.cta),
        primaryButton: asObject(communityContent.cta?.primaryButton),
      },
      joinForm: asObject(communityContent.joinForm),
    },
    contactContent: {
      ...minimalContent.contactContent,
      ...contactContent,
      hero: { ...minimalContent.contactContent.hero, ...asObject(contactContent.hero) },
      inquiryPaths: asArray(contactContent.inquiryPaths),
      form: {
        ...contactForm,
        fields: asArray(contactForm.fields),
      },
      contactInfo: {
        ...contactInfo,
        channels: asArray(contactInfo.channels),
      },
    },
    faqContent: {
      ...minimalContent.faqContent,
      ...faqContent,
      sections: asArray(faqContent.sections).map((section) => ({
        ...asObject(section),
        questions: asArray(section?.questions),
      })),
    },
    pressKitContent: {
      ...minimalContent.pressKitContent,
      ...pressKitContent,
      hero: { ...minimalContent.pressKitContent.hero, ...asObject(pressKitContent.hero) },
      sections: asObject(pressKitContent.sections),
    },
    blogContent: {
      ...minimalContent.blogContent,
      ...blogContent,
      posts: asArray(blogContent.posts),
    },
    bookLandingContent: asObject(rawContent.bookLandingContent),
  };
};

const normalizeBackendItemImage = (item) => ({
  ...item,
  id: item.id || item._id || item.slug,
  image: item.image || item.imageUrl || '',
});

export const normalizeBackendProducts = (payload) => {
  const items = Array.isArray(payload?.items) ? payload.items : [];
  return items.map((item) => ({
    ...normalizeBackendItemImage(item),
    buttonText: item.buttonText || 'Purchase Now',
  }));
};

export const normalizeBackendResources = (payload) => {
  const items = Array.isArray(payload?.items) ? payload.items : [];
  return items.map((item) => ({
    ...normalizeBackendItemImage(item),
    purchaseLink: item.purchaseLink || item.externalLink || item.buttons?.[0]?.link || '',
    buttons: item.buttons?.length ? item.buttons : undefined,
    reviews: item.reviews || [],
  }));
};

export const normalizeBackendBlogs = (payload) => {
  const items = Array.isArray(payload?.items) ? payload.items : [];
  return items.map((item) => ({
    ...item,
    id: item.id || item._id || item.slug,
    excerpt: typeof item.excerpt === 'string' ? item.excerpt : '',
    title: item.title || 'Untitled post',
    category: item.category || 'General',
    author: item.author || 'Sybil Obeng',
    readingTime: item.readingTime || '5 min read',
  }));
};

export const normalizeGallery = (payload, fallbackGallery) => {
  const items = Array.isArray(payload?.items) ? payload.items : [];
  if (!items.length) return fallbackGallery;
  return {
    ...(fallbackGallery || {}),
    images: items.map((item) => ({
      ...item,
      image: item.imageUrl,
      alt: item.alt || 'Bold Step Community image',
    })),
  };
};

export const mergePressKit = (pageCopy, pressKit) => {
  if (!pressKit) return pageCopy;
  return {
    ...pageCopy,
    hero: pageCopy?.hero || pressKit.hero || {},
    sections: {
      ...(pageCopy?.sections || {}),
      headshots: pressKit.headshots || pageCopy?.sections?.headshots || {},
      preferredHeadline: pressKit.preferredHeadline || pageCopy?.sections?.preferredHeadline || {},
      bios: pressKit.bios || pageCopy?.sections?.bios || {},
    },
  };
};

export const ContentProvider = ({ children }) => {
  const [content, setContent] = useState(() => readCachedSiteContent() || minimalContent);
  const [syncError, setSyncError] = useState('');
  const [lastSync, setLastSync] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    let isMounted = true;

    const loadBackendContent = async () => {
      if (!hasBackendApiConfig) return;

      setIsLoading(true);
      try {
        const [siteResult] = await Promise.allSettled([
          fetchBackendJson('/site-content'),
        ]);

        if (!isMounted) return;

        if (siteResult.status === 'fulfilled') {
          const nextContent = normalizeContentShape({
            ...minimalContent,
            ...(siteResult.value || {}),
          });
          setContent(nextContent);
          writeCachedSiteContent(nextContent);
          setLastSync(new Date().toISOString());
          setSyncError('');
        } else {
          setSyncError(siteResult.reason?.message || 'Backend content sync failed.');
        }
      } catch (error) {
        if (isMounted) {
          setSyncError(error.message || 'Backend content sync failed.');
        }
      } finally {
        if (isMounted) setIsLoading(false);
      }
    };

    loadBackendContent();

    return () => {
      isMounted = false;
    };
  }, []);

  const value = useMemo(
    () => ({
      content,
      syncInfo: {
        mode: hasBackendApiConfig ? 'backend-api' : 'backend-required',
        error: syncError,
        lastSync,
        isLoading,
      },
    }),
    [content, syncError, lastSync, isLoading],
  );

  return <ContentContext.Provider value={value}>{children}</ContentContext.Provider>;
};

export const useSiteContent = () => {
  const context = useContext(ContentContext);
  if (!context) throw new Error('useSiteContent must be used within a ContentProvider');
  return context;
};
